import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home, Clock, Calendar, ArrowLeft, ArrowRight } from "lucide-react";
import { Header, Footer } from "../../components/Layout";

const SITE_URL = "https://sinadicciones.org";

export const ArticleLayout = ({
  title,
  description,
  heroImage,
  readTime = "8 min",
  date = "2026",
  categories = [],
  children,
  prevArticle,
  nextArticle,
  canonicalPath, // optional override
  publishedDate, // ISO date string for Article schema
}) => {
  const location = useLocation();
  const path = canonicalPath || location.pathname || "/";
  const canonical = SITE_URL + path;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": heroImage,
    "author": { "@type": "Organization", "name": "SinAdicciones.org" },
    "publisher": {
      "@type": "Organization",
      "name": "SinAdicciones.org",
      "logo": { "@type": "ImageObject", "url": SITE_URL + "/favicon-512x512.png" },
    },
    "datePublished": publishedDate || (date.length === 4 ? date + "-01-01" : date),
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": SITE_URL + "/" },
      { "@type": "ListItem", "position": 2, "name": "Educación", "item": SITE_URL + "/educacion" },
      { "@type": "ListItem", "position": 3, "name": title, "item": canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{title + ' | Sin Adicciones Chile'}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="es_CL" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={heroImage} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      {/* Header unificado */}
      <Header />

      {/* Spacer para el header fixed */}
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/educacion" className="hover:text-emerald-400 transition-colors">
              Educación
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300 truncate max-w-[200px]">{title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950 z-10" />
        <img
          src={heroImage}
          alt={title}
          className="w-full h-[300px] md:h-[400px] object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-end">
          <div className="max-w-4xl mx-auto px-4 pb-8 w-full">
            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((cat, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full"
                >
                  {cat}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-slate-400 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {readTime} de lectura
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {date}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="prose prose-lg prose-invert prose-emerald max-w-none">
          {children}
        </article>

        {/* CTA Section */}
        <div className="mt-12 p-8 bg-gradient-to-r from-emerald-900/30 to-slate-800/50 rounded-2xl border border-emerald-500/20">
          <h3 className="text-2xl font-bold text-white mb-4">
            ¿Necesitas ayuda profesional?
          </h3>
          <p className="text-slate-300 mb-6">
            En SinAdicciones.org te conectamos con los mejores centros y profesionales de Chile.
            Solicita una asesoría personalizada y confidencial.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/asesoria"
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors"
            >
              Solicitar Asesoría Experta
            </Link>
            <Link
              to="/centros-de-rehabilitacion-drogas-chile"
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors"
            >
              Ver Centros
            </Link>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {prevArticle && (
            <Link
              to={prevArticle.path}
              className="p-6 bg-slate-800/50 hover:bg-slate-800 rounded-xl border border-slate-700 transition-colors group"
            >
              <span className="text-sm text-slate-400 flex items-center gap-2 mb-2">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Artículo anterior
              </span>
              <span className="text-white font-medium">{prevArticle.title}</span>
            </Link>
          )}
          {nextArticle && (
            <Link
              to={nextArticle.path}
              className="p-6 bg-slate-800/50 hover:bg-slate-800 rounded-xl border border-slate-700 transition-colors group md:text-right"
            >
              <span className="text-sm text-slate-400 flex items-center gap-2 mb-2 md:justify-end">
                Artículo siguiente
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="text-white font-medium">{nextArticle.title}</span>
            </Link>
          )}
        </div>
      </main>

      {/* Footer unificado */}
      <Footer />
    </div>
  );
};

export default ArticleLayout;
